/**
 * Branding Settings Tab Component
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Image, Settings, Upload } from 'lucide-react';
import { ThemeSettings } from './types';

interface BrandingTabProps {
  themeSettings: ThemeSettings;
  saving: boolean;
  onThemeSettingsChange: (settings: ThemeSettings) => void;
  onSave: () => void;
  onResetColors: () => void;
  onFileUpload: (fileType: string) => void;
}

export function BrandingTab({
  themeSettings,
  saving,
  onThemeSettingsChange,
  onSave,
  onResetColors,
  onFileUpload,
}: BrandingTabProps) {
  const updateTheme = (updates: Partial<ThemeSettings>) => {
    onThemeSettingsChange({ ...themeSettings, ...updates });
  };

  return (
    <div className="space-y-4">
      {/* Login Page Customization */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="h-5 w-5" />
            Login Page Customization
          </CardTitle>
          <CardDescription>
            Customize logos, colors, and images for the login page
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Sign-in page heading — editable override for the default "<Platform Name> Portal" */}
          <div className="space-y-2">
            <Label htmlFor="loginTitle">Sign-in Page Heading</Label>
            <Input
              id="loginTitle"
              type="text"
              value={themeSettings.loginTitle}
              onChange={(e) => updateTheme({ loginTitle: e.target.value })}
              placeholder="e.g. Your Supplier Portal"
            />
            <p className="text-xs text-muted-foreground">
              Shown below the logo on the sign-in page. Leave blank to use
              "<span className="font-mono">{'{Platform Name}'} Portal</span>".
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 pt-4 border-t">
            {/* Company Light Logo */}
            <div className="space-y-2">
              <Label htmlFor="companyLogo">Company Light Logo</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Upload your company logo (Light mode)
                </p>
                <Button variant="outline" size="sm" onClick={() => onFileUpload('companyLogo')}>
                  Choose File
                </Button>
              </div>
            </div>

            {/* Company Dark Logo */}
            <div className="space-y-2">
              <Label htmlFor="companyDarkLogo">Company Dark Logo</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Upload your company logo (Dark mode)
                </p>
                <Button variant="outline" size="sm" onClick={() => onFileUpload('companyDarkLogo')}>
                  Choose File
                </Button>
              </div>
            </div>

            {/* Login Image */}
            <div className="space-y-2">
              <Label htmlFor="loginImage">Login Background Image</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Upload login page background image
                </p>
                <Button variant="outline" size="sm" onClick={() => onFileUpload('loginImage')}>
                  Choose File
                </Button>
              </div>
            </div>

            {/* Header Logo */}
            <div className="space-y-2">
              <Label htmlFor="headerLogo">Header Company Logo</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Upload header logo for navigation
                </p>
                <Button variant="outline" size="sm" onClick={() => onFileUpload('headerLogo')}>
                  Choose File
                </Button>
              </div>
            </div>
          </div>

          {/* Color Pickers */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
            <div className="space-y-2">
              <Label htmlFor="backgroundColor">Background Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  id="backgroundColor"
                  value={themeSettings.backgroundColor}
                  onChange={(e) => updateTheme({ backgroundColor: e.target.value })}
                  className="h-10 w-20"
                />
                <Input
                  type="text"
                  value={themeSettings.backgroundColor}
                  onChange={(e) => updateTheme({ backgroundColor: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="loginColor">Login Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  id="loginColor"
                  value={themeSettings.loginColor}
                  onChange={(e) => updateTheme({ loginColor: e.target.value })}
                  className="h-10 w-20"
                />
                <Input
                  type="text"
                  value={themeSettings.loginColor}
                  onChange={(e) => updateTheme({ loginColor: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fontColor">Font Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  id="fontColor"
                  value={themeSettings.fontColor}
                  onChange={(e) => updateTheme({ fontColor: e.target.value })}
                  className="h-10 w-20"
                />
                <Input
                  type="text"
                  value={themeSettings.fontColor}
                  onChange={(e) => updateTheme({ fontColor: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="buttonColor">Button Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  id="buttonColor"
                  value={themeSettings.buttonColor}
                  onChange={(e) => updateTheme({ buttonColor: e.target.value })}
                  className="h-10 w-20"
                />
                <Input
                  type="text"
                  value={themeSettings.buttonColor}
                  onChange={(e) => updateTheme({ buttonColor: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button onClick={onSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Login Page Settings'}
            </Button>
            <Button variant="outline" disabled={saving} onClick={onResetColors}>
              {saving ? 'Resetting...' : 'Reset Colors to Default'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main Company Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Main Company Details
          </CardTitle>
          <CardDescription>
            Company information and contact details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="companyName">Platform Name</Label>
            <Input id="companyName" value={themeSettings.companyName} onChange={(e) => updateTheme({ companyName: e.target.value })} placeholder="Helix" />
            <p className="text-sm text-muted-foreground">This name appears in the header, login page, and email communications</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="contactEmail">Contact Email</Label>
            <Input id="contactEmail" type="email" value={themeSettings.contactEmail} onChange={(e) => updateTheme({ contactEmail: e.target.value })} placeholder="contact@company.com" />
            <p className="text-sm text-muted-foreground">Main contact email for the organisation</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="companyPhone">Phone Number</Label>
            <Input id="companyPhone" type="tel" value={themeSettings.companyPhone} onChange={(e) => updateTheme({ companyPhone: e.target.value })} placeholder="+44 123 456 7890" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="companyAddress">Address</Label>
            <Input id="companyAddress" value={themeSettings.companyAddress} onChange={(e) => updateTheme({ companyAddress: e.target.value })} placeholder="Company address" />
          </div>
          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Company Details'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
